import { useEffect, useState } from 'react';
import { api, ErroApi } from '../../api/cliente';
import type { LogAdmin } from '../../api/tipos';
import { FileClock } from 'lucide-react';
import {
  Aviso,
  Cartao,
  Esqueleto,
  Etiqueta,
  Tabela,
  Td,
  Th,
  Vazio,
  type TomEtiqueta,
} from '../../componentes/ui';
import { Pagina, TituloPagina } from '../../componentes/Pagina';
import { dataHora } from '../../util/formato';
import css from './AuditoriaAdmin.module.css';

/** Como cada ação se apresenta: rótulo legível e peso visual. */
const ACOES: Record<string, { rotulo: string; tom: TomEtiqueta }> = {
  'usuario.bloquear': { rotulo: 'Bloqueou conta', tom: 'erro' },
  'usuario.desbloquear': { rotulo: 'Desbloqueou conta', tom: 'ok' },
  'usuario.promover': { rotulo: 'Promoveu a administrador', tom: 'alerta' },
  'usuario.rebaixar': { rotulo: 'Rebaixou para usuário', tom: 'info' },
  'usuario.verificar-email': { rotulo: 'Verificou e-mail', tom: 'info' },
  'usuario.resetar-senha': { rotulo: 'Redefiniu senha', tom: 'alerta' },
  'credito.lancar': { rotulo: 'Lançou crédito', tom: 'alerta' },
  'api-key.revogar': { rotulo: 'Revogou chave de API', tom: 'erro' },
};

/**
 * Trilha de auditoria: o que os administradores fizeram.
 *
 * Um painel que move dinheiro e bloqueia contas precisa registrar quem fez o
 * quê — inclusive porque, sem isso, não há como distinguir um erro de operação
 * de um acesso indevido depois que o estrago já apareceu.
 *
 * Para dinheiro, o razão do cliente continua sendo a fonte autoritativa: lá o
 * lançamento é gravado na mesma transação do saldo. Esta tela responde "quem
 * clicou", não "quanto tem em conta".
 */
export function AuditoriaAdmin() {
  const [itens, setItens] = useState<LogAdmin[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    api<LogAdmin[]>('/admin/auditoria?limite=100')
      .then(setItens)
      .catch((err: unknown) =>
        setErro(
          err instanceof ErroApi
            ? err.message
            : 'Falha ao carregar a auditoria.',
        ),
      );
  }, []);

  return (
    <Pagina largura="larga">
      <TituloPagina
        titulo="Auditoria"
        descricao="As últimas 100 ações administrativas, com quem executou e de onde."
      />

      {erro && <Aviso>{erro}</Aviso>}

      <Cartao padding={0}>
        {!itens ? (
          <div className={css.carregando}>
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
          </div>
        ) : itens.length === 0 ? (
          <Vazio
            icone={<FileClock />}
            titulo="Nada registrado ainda"
            descricao="Bloqueios, créditos manuais e revogações aparecem aqui assim que acontecerem."
          />
        ) : (
          <Tabela rotulo="Ações registradas">
            <thead>
              <tr>
                <Th>Quando</Th>
                <Th>Quem</Th>
                <Th>Ação</Th>
                <Th>Sobre</Th>
                <Th>Detalhe</Th>
                <Th>IP</Th>
              </tr>
            </thead>
            <tbody>
              {itens.map((l) => {
                const acao = ACOES[l.acao];
                return (
                  <tr key={l.id}>
                    <Td className={css.apoio}>{dataHora(l.createdAt)}</Td>
                    <Td>
                      <span className={css.quem}>{l.adminEmail}</span>
                    </Td>
                    <Td>
                      <Etiqueta tom={acao?.tom ?? 'neutro'}>
                        {acao?.rotulo ?? l.acao}
                      </Etiqueta>
                    </Td>
                    <Td className={css.apoio}>{l.alvoEmail ?? '—'}</Td>
                    <Td>
                      <span className={css.detalhe}>{l.detalhe ?? '—'}</span>
                    </Td>
                    <Td className={css.ip}>{l.ip ?? '—'}</Td>
                  </tr>
                );
              })}
            </tbody>
          </Tabela>
        )}
      </Cartao>
    </Pagina>
  );
}
